import { lightFormat } from 'date-fns';
import {
  ADLER_COMPONENT_FIELD_COUNT,
  ADLER_END_MARKER,
  ADLER_HEADER_FIELD_COUNT,
  ADLER_MAX_COMPONENTS,
  ADLER_TOTAL_FIELD_COUNT,
  adlerColorantCode,
  adlerQuantity,
  parseAdlerQrPayload,
  tintingResultMap,
} from './AdlerQrInput';

const ADLER_TOTAL_QUANTITY_FIELD_INDEX = 12;
const ADLER_TOTAL_UNIT_FIELD_INDEX = 13;
const ADLER_OPERATION_TYPE_FIELD_INDEX = 14;

function adlerDateTime(value) {
  const parsed = value ? new Date(value) : new Date();
  const date = Number.isNaN(parsed.valueOf()) ? new Date() : parsed;

  return {
    date: lightFormat(date, 'yyyyMMdd'),
    time: lightFormat(date, 'HHmmss'),
  };
}

function actualQuantityForUnit(result, unit) {
  if (!result) return null;
  if (unit === 'g') return result.actual_mass;
  if (unit === 'kg') return result.actual_mass / 1000;
  if (unit === 'ml') return result.actual_volume;
  if (unit === 'l') return result.actual_volume / 1000;
  return null;
}

function adlerQuantityDecimals(result, unit) {
  if (unit === 'g') return result?.scale_resolution ?? 3;
  if (unit === 'kg') return (result?.scale_resolution ?? 3) + 3;
  if (unit === 'l') return 6;
  return 3;
}

function generatedAdlerUnit(shotFormatter) {
  return shotFormatter?.unit?.gravimetric ? 'g' : 'ml';
}

function generatedAdlerQuantity({
  volume,
  mass,
  specificgravity,
  unit,
  decimals = 3,
}) {
  const quantity =
    unit === 'g'
      ? (mass ?? volume * specificgravity)
      : (volume ?? mass / specificgravity);
  return adlerQuantity(quantity, decimals);
}

function updateImportedAdlerQr(order) {
  const adlerQr = parseAdlerQrPayload(order.source_barcode);
  if (!adlerQr) return null;

  const fields = order.source_barcode.trim().split(';');
  const resultMap = tintingResultMap(order.tinting_result);

  adlerQr.components
    .filter((component) => !component.isBase)
    .forEach((component) => {
      const result = resultMap.get(adlerColorantCode(component.name));
      if (!result) {
        fields[component.quantityFieldIndex] = '';
        return;
      }

      const actual = actualQuantityForUnit(result, component.unit);
      const quantity = adlerQuantity(
        actual,
        adlerQuantityDecimals(result, component.unit)
      );

      fields[component.quantityFieldIndex] = quantity ?? '';
    });

  fields[adlerQr.endMarkerIndex] = ADLER_END_MARKER;
  return fields.join(';');
}

function generatedHeaderFields(order) {
  const { item, product } = order;
  const { date, time } = adlerDateTime(order.orderdate);
  const fields = Array(ADLER_TOTAL_FIELD_COUNT).fill('');

  fields[0] = '@';
  fields[1] = date;
  fields[2] = time;
  fields[3] = 'DISPENSER';
  fields[4] = product?.productcode || '';
  fields[5] = item.colourcode || '';
  fields[6] = item.colourname || '';
  fields[ADLER_OPERATION_TYPE_FIELD_INDEX] = 'NEW';
  fields[15] = item.productname || product?.productzname || '';
  fields[ADLER_TOTAL_FIELD_COUNT - 1] = ADLER_END_MARKER;

  return fields;
}

function generatedBaseComponent(order, unit) {
  const baseName = (
    order.base?.abstractbase?.abasecode ||
    order.item?.abasecode ||
    order.item?.basecode ||
    ''
  ).trim();
  if (!baseName) return null;

  return {
    name: baseName,
    quantity:
      generatedAdlerQuantity({
        volume: order.can?.basevolume,
        specificgravity: order.base?.specificgravity,
        unit,
      }) || '0',
    unit,
  };
}

function generatedColorantComponents(order, unit) {
  const resultMap = tintingResultMap(order.tinting_result);
  return (order.item?.cnts || []).map((cnt) => {
    const name = adlerColorantCode(cnt.cntcode);
    const result = resultMap.get(name);
    const quantity = result
      ? generatedAdlerQuantity({
          volume: result.actual_volume,
          mass: result.actual_mass,
          specificgravity: cnt.specificgravity,
          unit,
          decimals: unit === 'g' ? (result.scale_resolution ?? 3) : 3,
        })
      : '';

    return {
      name,
      quantity,
      unit,
    };
  });
}

function generateAdlerQrFromOrder(order, shotFormatter) {
  const unit = generatedAdlerUnit(shotFormatter);
  const fields = generatedHeaderFields(order);
  const baseComponent = generatedBaseComponent(order, unit);
  if (!baseComponent) return '';

  const components = [
    baseComponent,
    ...generatedColorantComponents(order, unit),
  ]
    .filter((component) => component?.name)
    .slice(0, ADLER_MAX_COMPONENTS);

  const totalQuantity = components.reduce(
    (sum, component) => sum + Number(component.quantity || 0),
    0
  );
  fields[ADLER_TOTAL_QUANTITY_FIELD_INDEX] =
    adlerQuantity(totalQuantity, 0) || '0';
  fields[ADLER_TOTAL_UNIT_FIELD_INDEX] = unit;

  components.forEach((component, index) => {
    const fieldIndex =
      ADLER_HEADER_FIELD_COUNT + index * ADLER_COMPONENT_FIELD_COUNT;
    fields[fieldIndex] = component.name;
    fields[fieldIndex + 1] = component.quantity;
    fields[fieldIndex + 2] = component.unit;
  });

  return fields.join(';');
}

export function generateAdlerQrCode(order, shotFormatter) {
  if (!order) return '';

  if (order.source_barcode) {
    try {
      return updateImportedAdlerQr(order) || '';
    } catch {
      return '';
    }
  }

  return generateAdlerQrFromOrder(order, shotFormatter);
}
